import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { typeColors, typeLabels, TypeVariant } from './types';
import { typography, spacing, colors } from '../../tokens';

export interface TypeTextProps {
  /**
   * The type variant that determines the color and label
   */
  variant: TypeVariant;

  /**
   * Optional custom label text (overrides default)
   */
  label?: string;

  /**
   * Callback when pressed
   */
  onPress?: () => void;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const TypeText: React.FC<TypeTextProps> = ({
  variant,
  label,
  onPress,
  disabled = false,
  testID,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const backgroundColor = typeColors[variant];
  const displayLabel = label || typeLabels[variant];

  const content = (
    <View
      style={[
        styles.container,
        {
          backgroundColor,
          opacity: disabled ? 0.5 : isHovered ? 0.9 : 1,
        },
      ]}
    >
      <Text style={styles.label}>{displayLabel}</Text>
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View testID={testID} accessibilityRole="text">
      {content}
    </View>
  );
};

TypeText.displayName = 'TypeText';

const styles = StyleSheet.create({
  container: {
    minWidth: 110,
    height: 40,
    borderRadius: 4,
    paddingHorizontal: spacing.md, // 16px
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    ...typography.body.sm,
    fontSize: 12,
    fontWeight: '400',
    color: colors.brand.white, // #FCFCFC
  },
});
